import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  selectLocalizedLanguages,
  selectTrainingLocalizationState,
  UpdateSetOfKeys,
} from "../../app/slices/TrainingLocalizationSlice";
import Interaction from "../../models/interactions/Interaction";
import { getKeysFromInteraction } from "../../helpers/KeysHelper";
import { selectReferenceLanguage } from "../../app/slices/TrainingSlice";
import {
  TranslationFailedError,
  TranslationService,
} from "../../services/TranslationService";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import {
  beginHistoryTransaction,
  cancelHistoryTransaction,
  commitHistoryTransaction,
} from "../../app/slices/HistorySlice";
import {
  keysToSend,
  readValue,
  TranslationScope,
} from "./translationScope";

export interface TranslateInteractionOptions {
  /** Language the texts are read from. Defaults to the training's reference language. */
  from?: string;
  /** Languages written to. Defaults to every localized language except the source. */
  to?: string[];
  /** Defaults to "all", which is what the plain Translate all button does. */
  scope?: TranslationScope;
}

export default function useInteractionsTranslation() {
  const locState = useSelector(selectTrainingLocalizationState);
  const currentLanguages = useSelector(selectLocalizedLanguages);
  const referenceLanguage = useSelector(selectReferenceLanguage);
  const token = useSelector(selectToken);
  const dispatch = useDispatch();

  const defaultTargetsFor = (source: string) =>
    currentLanguages
      .map((language) => language.shortcode)
      .filter((shortcode) => shortcode !== source);

  /**
   * Translates one interaction's keys as a single undoable step.
   *
   * The whole run is one history transaction: the key-update action is an
   * undoable command, so without one, translating four languages would cost four
   * presses of undo and could be abandoned half-way. Languages are awaited in
   * sequence so the caller learns the outcome — this used to be a
   * `forEach(async …)`, which threw the promises away and resolved before
   * anything had landed.
   */
  const translateAll = async (
    interaction: Interaction,
    options: TranslateInteractionOptions = {}
  ) => {
    const from = options.from ?? referenceLanguage;
    const targets = options.to ?? defaultTargetsFor(from);
    const scope = options.scope ?? "all";

    const keys: string[] = getKeysFromInteraction(interaction, "key_");

    const transaction = { id: `translate-interaction-${uuidv4()}` };
    dispatch(beginHistoryTransaction(transaction));

    try {
      for (const target of targets) {
        if (target === from) continue;
        await translateForLanguage(target, keys, from, scope);
      }
      dispatch(commitHistoryTransaction(transaction));
    } catch (error) {
      dispatch(cancelHistoryTransaction(transaction));
      throw error;
    }
  };

  const translateForLanguage = async (
    target: string,
    keys: string[],
    from: string,
    scope: TranslationScope
  ) => {
    const selectedKeys = keysToSend(
      locState.localizedEntries,
      keys,
      target,
      scope
    );

    if (selectedKeys.length === 0) return;

    const texts = selectedKeys.map((key) =>
      readValue(locState.localizedEntries, key, from)
    );

    const results = await TranslationService.TranslateTexts(
      token!,
      texts,
      from,
      target
    );

    // The service already rejects on a length mismatch. This second check keeps
    // the reducer safe regardless of what any future caller hands it: values are
    // paired with keys by index, and a short array would write `undefined` over
    // existing translations rather than failing.
    if (results.length !== selectedKeys.length) {
      throw new TranslationFailedError(
        from,
        target,
        `expected ${selectedKeys.length} translations, received ${results.length}`
      );
    }

    dispatch(
      UpdateSetOfKeys({
        lang: target,
        keys: selectedKeys,
        values: results,
      })
    );
  };

  return { translateAll };
}
